import { useCallback } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';

import { useDockerSnapshotContainer } from '@/react/docker/queries/useDockerSnapshotContainer';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { notifySuccess } from '@/portainer/services/notifications';

import { PageHeader } from '@@/PageHeader';
import { LogViewer } from '@@/LogViewer';
import { Button } from '@@/buttons';
import { Loading } from '@@/Widget';
import { ControllerState } from '@@/LogViewer/hooks/useControllerStates';

import { DockerSnapshotPanel } from '../../components/SnapshotPanel';
import { useCollectLogsMutation } from '../../edge-stacks/ItemView/EnvironmentsDatatable/useCollectLogsMutation';
import {
  LogStatus,
  useLogsStatus,
} from '../../edge-stacks/ItemView/EnvironmentsDatatable/useLogsStatus';
import { EDGE_STACK_ID_LABEL } from '../../edge-stacks/types';

import { getContainerLogs } from './useAsyncContainerLogs';

export function LogView() {
  const {
    params: { endpointId: environmentId, containerId },
  } = useCurrentStateAndParams();

  if (!environmentId || !containerId) {
    throw new Error('Missing environmentId or containerId parameters');
  }

  const environmentQuery = useEnvironment(environmentId);

  const containerQuery = useDockerSnapshotContainer(environmentId, containerId);
  const collectLogsMutation = useCollectLogsMutation();

  const edgeStackId = parseInt(
    containerQuery.data?.Labels?.[EDGE_STACK_ID_LABEL] ?? '0',
    10
  );

  const logsStatusQuery = useLogsStatus(
    {
      edgeStackId,
      environmentId,
      containerId,
    },
    { enabled: !containerQuery.isLoading && !!containerQuery.data }
  );

  const getLogsFn = useCallback(() => {
    if (!logsStatusQuery.isFetching && logsStatusQuery.data === 'collected') {
      return getContainerLogs({ edgeStackId, environmentId, containerId });
    }
    return Promise.resolve('');
  }, [
    logsStatusQuery.isFetching,
    logsStatusQuery.data,
    containerId,
    edgeStackId,
    environmentId,
  ]);

  const handleCollectLogs = useCallback(
    ({
      since,
      tail,
      until,
    }: Pick<ControllerState, 'since' | 'until' | 'tail'>) => {
      collectLogsMutation.mutate(
        {
          edgeStackId,
          environmentId,
          containerId,
          since,
          until,
          tail: `${tail}`,
        },
        {
          onSuccess: () => notifySuccess('Success', 'Logs Collection started'),
        }
      );
    },
    [collectLogsMutation, containerId, edgeStackId, environmentId]
  );

  if (!environmentQuery.data || !containerQuery.data || !logsStatusQuery.data) {
    return null;
  }

  const { data: environment } = environmentQuery;
  const { data: container } = containerQuery;

  const containerName = container.Names[0];

  return (
    <>
      <PageHeader
        title="Container logs"
        breadcrumbs={[
          { label: 'Async browse' },
          {
            label: environment.Name,
            link: 'edge.browse.dashboard',
            linkParams: { environmentId },
          },
          { label: 'Containers', link: '^.^' },
          { label: containerName, link: '^' },
          { label: 'Logs' },
        ]}
        reload
      >
        <DockerSnapshotPanel environment={environment} />
      </PageHeader>

      <LogViewer
        resourceType="docker-container"
        resourceName={containerName}
        getLogsFn={getLogsFn}
        hideTimestamp
        renderHeader={({ controllerState: { since, until, tail } }) => (
          <Button
            title="Retrieve logs"
            onClick={() => handleCollectLogs({ since, until, tail })}
            data-cy="edge-stack-logs-collect-button"
            className="mx-auto"
          >
            {getButtonText(logsStatusQuery.data)}
          </Button>
        )}
        renderAlternateBody={() => {
          switch (logsStatusQuery.data) {
            case 'idle':
              return (
                <div className="mt-28 mx-auto text-center">
                  <span>Logs for this container are not collected.</span>
                  <br />
                  <span>
                    Select a start / end date and start the log collection.
                  </span>
                </div>
              );
            case 'pending':
              return (
                <div className="mt-28 mx-auto text-center">
                  <span>Logs collection is in progress</span>
                  <Loading />
                </div>
              );
            default:
              return null;
          }
        }}
      />
    </>
  );
}

function getButtonText(status: LogStatus) {
  switch (status) {
    case 'idle':
      return 'Start log collection';
    default:
      return 'Restart log collection';
  }
}
